// frontend/src/components/Video.js - VERSION SIMPLIFIÉE
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Button, Spinner, Alert, Container, Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './Videos.css';
import { BASE_URL } from '../constants';

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [error, setError] = useState('');
  const [videoError, setVideoError] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  
  const { playlistId } = useParams();
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const videoRef = useRef(null);

  // ✅ FONCTION : Obtenir l'URL complète
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    
    if (filePath.startsWith('http') || filePath.startsWith('https')) {
      return filePath;
    }
    
    const baseUrl = BASE_URL || '';
    
    const cleanPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
    return `${baseUrl}${cleanPath}`;
  };

  // ✅ FONCTIONS UTILITAIRES
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const detectContentType = (video) => {
    if (!video || !video.mimetype) return 'other';
    const mime = video.mimetype.toLowerCase();
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('image/')) return 'image';
    if (mime === 'application/pdf') return 'pdf';
    return 'other';
  };

  // ✅ LECTEUR VIDÉO UNIQUE - HTML5 NATIF
  const renderVideoPlayer = (video) => {
    const fileUrl = getFileUrl(video.path);
    
    return (
      <div className="video-container">
        {/* Informations de la vidéo */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">{video.name}</h4>
            <Badge bg="primary">{video.mimetype}</Badge>
            <span className="ms-2 text-muted">
              {formatFileSize(video.size)}
            </span>
          </div>
          <Button 
            variant="outline-primary" 
            href={fileUrl} 
            download
            className="d-flex align-items-center"
          >
            <i className="fas fa-download me-2"></i>
            Télécharger
          </Button>
        </div>

        {/* Messages d'erreur */}
        {videoError && (
          <Alert variant="danger" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {videoError}
          </Alert>
        )}

        {/* Lecteur HTML5 natif uniquement */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <video
              ref={videoRef}
              controls
              width="100%"
              height="auto"
              crossOrigin="anonymous"
              style={{ 
                borderRadius: '8px', 
                backgroundColor: '#000',
                maxHeight: '70vh'
              }}
              onError={(e) => {
                console.error('❌ Native video error:', e);
                const videoElement = e.target;
                setVideoError(`Erreur de chargement - ${videoElement.error?.message || 'Fichier corrompu ou format non supporté'}`);
              }}
              onLoadStart={() => {
                //console.log('📥 Chargement de la vidéo en cours...');
                setVideoError('');
              }}
              onLoadedData={() => {
                //console.log('✅ Données vidéo chargées');
                setVideoError('');
              }}
              onCanPlay={() => {
                //console.log('🎬 Vidéo prête à être lue');
                setVideoError('');
              }}
              onStalled={() => {
                //console.log('⚠️ Lecture interrompue');
                setVideoError('Lecture interrompue - problème de réseau');
              }}
              preload="metadata"
            >
              <source src={fileUrl} type={video.mimetype} />
              Votre navigateur ne supporte pas la lecture vidéo HTML5.
              <br />
              <a href={fileUrl} download>Téléchargez la vidéo</a> pour la visualiser.
            </video>
          </Card.Body>
        </Card>

        {/* Contrôles supplémentaires */}
        <div className="video-controls mt-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="text-muted small">
              <div><strong>Format:</strong> {video.mimetype}</div>
              <div><strong>Taille:</strong> {formatFileSize(video.size)}</div>
              <div><strong>Type:</strong> {detectContentType(video)}</div>
            </div>
            <Button 
              variant="outline-secondary"
              onClick={() => window.open(fileUrl, '_blank')}
              size="sm"
            >
              <i className="fas fa-external-link-alt me-2"></i>
              Ouvrir dans un nouvel onglet
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ AFFICHAGE DES AUTRES TYPES DE FICHIERS
  const renderFileContent = (video) => {
    if (!video) return <Alert variant="info">Aucun fichier sélectionné</Alert>;
    
    const fileType = detectContentType(video);
    const fileUrl = getFileUrl(video.path);
    
    switch (fileType) {
      case 'video':
        return renderVideoPlayer(video);
      case 'image':
        return (
          <div className="text-center">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-3">
                <img
                  src={fileUrl}
                  alt={video.name}
                  className="img-fluid rounded"
                  style={{ maxHeight: '500px', maxWidth: '100%' }}
                />
                <div className="mt-3">
                  <Button variant="primary" href={fileUrl} download>
                    <i className="fas fa-download me-2"></i>
                    Télécharger l'image
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        );
      default:
        return (
          <div className="text-center py-5">
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <i className="fas fa-file fa-4x text-secondary mb-3"></i>
                <h4>{video.name}</h4>
                <p className="text-muted mb-3">
                  {video.mimetype} • {formatFileSize(video.size)}
                </p>
                <Button variant="primary" href={fileUrl} download>
                  <i className="fas fa-download me-2"></i>
                  Télécharger le fichier
                </Button>
              </Card.Body>
            </Card>
          </div>
        );
    }
  };

  // ✅ CHARGEMENT DES VIDÉOS
  const fetchVideos = useCallback(async () => {
    if (!userInfo) {
      navigate('/login');
      return;
    }

    try {
      setFetchLoading(true);
      setError('');
      
      const baseUrl = userInfo?.isFormateur || userInfo?.isAdmin 
        ? '/api/playlists/formateur' 
        : '/api/playlists';

      const response = await axios.get(`${baseUrl}/${playlistId}/videos`, {
        withCredentials: true,
      });
      
      //console.log('✅ Videos loaded:', response.data);
      setVideos(response.data);
      if (response.data.length > 0) {
        setSelectedVideo(response.data[0]);
      }
      
    } catch (error) {
      console.error('❌ Error fetching videos:', error);
      setError('Erreur lors du chargement des fichiers');
    } finally {
      setFetchLoading(false);
    }
  }, [playlistId, userInfo, navigate]);

  useEffect(() => {
    if (!playlistId || !userInfo) {
      navigate('/login');
      return;
    }
    fetchVideos();
  }, [playlistId, userInfo, navigate, fetchVideos]);

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setVideoError('');
  };

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Fichiers de la playlist</h2>
        <div className="d-flex gap-2">
          <Link to={`/formateur/playlists/${playlistId}/add-video`}>
            <Button variant="success">
              <i className="fas fa-plus me-2"></i>
              Ajouter du contenu
            </Button>
          </Link>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2"></i>
            Retour
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {fetchLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Chargement des fichiers...</p>
        </div>
      ) : (
        <Row>
          {/* Aperçu principal */}
          <div className="col-lg-8 mb-4">
            {selectedVideo ? (
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  {renderFileContent(selectedVideo)}
                </Card.Body>
              </Card>
            ) : (
              <Alert variant="info" className="text-center">
                <i className="fas fa-play-circle fa-2x mb-3 text-muted"></i>
                <h5>Sélectionnez un fichier</h5>
                <p className="mb-0">Choisissez un fichier dans la liste pour le visualiser</p>
              </Alert>
            )}
          </div>

          {/* Liste des fichiers */}
          <div className="col-lg-4">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light">
                <h5 className="mb-0">Liste des fichiers ({videos.length})</h5>
              </Card.Header>
              <Card.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {videos.length === 0 ? (
                  <Alert variant="info" className="text-center mb-0">
                    <i className="fas fa-folder-open fa-2x mb-2 text-muted"></i>
                    <p>Aucun fichier dans cette playlist</p>
                    <Link to={`/formateur/playlists/${playlistId}/add-video`}>
                      <Button variant="primary" size="sm">
                        <i className="fas fa-plus me-1"></i>
                        Ajouter le premier fichier
                      </Button>
                    </Link>
                  </Alert>
                ) : (
                  videos.map((video, index) => (
                    <div
                      key={video._id || index}
                      className={`card mb-2 cursor-pointer ${
                        selectedVideo?._id === video._id 
                          ? 'border-primary bg-light' 
                          : 'border-light'
                      }`}
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => handleVideoClick(video)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div className="card-body py-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <h6 className="card-title mb-1 text-truncate" title={video.name}>
                              {video.name}
                            </h6>
                            <small className="text-muted">
                              {video.mimetype} • {formatFileSize(video.size)}
                            </small>
                          </div>
                          <i className={`fas ${
                            detectContentType(video) === 'video' ? 'fa-play-circle text-primary' : 
                            detectContentType(video) === 'image' ? 'fa-image text-success' : 
                            'fa-file text-secondary'
                          } ms-2`}></i>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </div>
        </Row>
      )}
    </Container>
  );
};

export default Videos;